import type { EnrichmentState } from './hooks/useMediaAiEnrichment';
import { Badge } from '@/components/ui';
import type { DamageItem, DamageResult } from './types';

export interface AiSummaryPanelProps {
  enrichmentByPhotoId: Record<string, EnrichmentState>;
}

interface SeverityBucket {
  key: 'severe' | 'moderate' | 'minor' | 'none';
  label: string;
  barColor: string;
}

const SEVERITY_BUCKETS: SeverityBucket[] = [
  { key: 'severe', label: 'Severe', barColor: 'bg-red-500' },
  { key: 'moderate', label: 'Moderate', barColor: 'bg-orange-500' },
  { key: 'minor', label: 'Minor', barColor: 'bg-yellow-500' },
  { key: 'none', label: 'None', barColor: 'bg-gray-400' },
];

// Worst-first ranking used to pick the surviving entry when the same part is
// flagged on more than one photo, and to sort the unique-parts list.
const SEVERITY_RANK: Record<string, number> = { severe: 3, moderate: 2, minor: 1, none: 0 };

function severityRank(severity: string): number {
  return SEVERITY_RANK[severity] ?? -1;
}

function severityLabel(severity: string): string {
  return severity.charAt(0).toUpperCase() + severity.slice(1);
}

function severityBadgeVariant(severity: string): 'error' | 'warning' | 'default' {
  if (severity === 'severe') return 'error';
  if (severity === 'moderate' || severity === 'minor') return 'warning';
  return 'default';
}

function getAssessedDamages(enrichmentByPhotoId: Record<string, EnrichmentState>): DamageResult[] {
  return Object.values(enrichmentByPhotoId)
    .filter(state => state.status === 'success' && !!state.results.damage)
    .map(state => state.results.damage as DamageResult);
}

// Dedup engine: flattens every damaged part across all assessed photos and
// collapses repeats of the same part_name into one entry, keeping whichever
// instance has the higher severity (ties keep the first-seen instance) —
// the worse assessment is the safer one to surface to an adjuster.
export function getUniqueDamagedParts(assessed: DamageResult[]): DamageItem[] {
  const byPartName = new Map<string, DamageItem>();

  for (const result of assessed) {
    for (const item of result.damages) {
      const existing = byPartName.get(item.part_name);
      if (!existing || severityRank(item.severity) > severityRank(existing.severity)) {
        byPartName.set(item.part_name, item);
      }
    }
  }

  return Array.from(byPartName.values()).sort(
    (a, b) => severityRank(b.severity) - severityRank(a.severity)
  );
}

export function AiSummaryPanel({ enrichmentByPhotoId }: AiSummaryPanelProps) {
  const assessed = getAssessedDamages(enrichmentByPhotoId);
  const aiBase = Math.max(1, assessed.length);

  const totalIssues = assessed.reduce((sum, damage) => sum + damage.damages.length, 0);
  const totalCost = assessed.reduce((sum, damage) => sum + (damage.estimated_total_cost?.recommended_total ?? 0), 0);
  const uniqueParts = getUniqueDamagedParts(assessed);

  const seenCaptions = new Set<string>();

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {SEVERITY_BUCKETS.map(bucket => {
          const count = assessed.filter(damage => damage.overall_severity === bucket.key).length;
          const percentage = Math.round((count / aiBase) * 100);
          const caption = `${count} · ${percentage}%`;
          // Split the number into a nested span for repeat values (e.g. two
          // buckets tied at "1 · 25%") so each bucket keeps its own DOM node
          // without producing indistinguishable duplicate text elements.
          const isRepeatCaption = seenCaptions.has(caption);
          seenCaptions.add(caption);

          return (
            <div key={bucket.key} className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">{bucket.label}</span>
                <span className="text-gray-500">
                  {isRepeatCaption ? (
                    <>
                      {count} · <span>{percentage}%</span>
                    </>
                  ) : (
                    caption
                  )}
                </span>
              </div>
              <div
                role="progressbar"
                aria-label={bucket.label}
                aria-valuenow={percentage}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-2 w-full overflow-hidden rounded-full bg-gray-200"
              >
                <div className={`h-full ${bucket.barColor} transition-all duration-300 ease-in-out`} style={{ width: `${percentage}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {uniqueParts.length > 0 && (
        <div className="space-y-2 border-t border-gray-200 pt-3">
          <p className="text-xs font-medium text-gray-500">Damaged parts ({uniqueParts.length})</p>
          <ul className="space-y-1.5">
            {uniqueParts.map((part) => (
              <li key={part.part_name} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate font-medium text-gray-900">{part.part_name}</span>
                  <Badge variant={severityBadgeVariant(part.severity)} size="sm">
                    {severityLabel(part.severity)}
                  </Badge>
                </span>
                {part.estimated_cost?.recommended_cost != null && (
                  <span className="flex-shrink-0 font-medium text-gray-700">
                    ₹{part.estimated_cost.recommended_cost.toLocaleString('en-IN')}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-3">
        <div>
          <p className="text-xs font-medium text-gray-500">Total issues</p>
          <p className="text-lg font-semibold text-gray-900">{totalIssues}</p>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500">Est. repair cost</p>
          <p className="text-lg font-semibold text-gray-900">₹{totalCost.toLocaleString('en-IN')}</p>
        </div>
      </div>
    </div>
  );
}
